/**
 * smokeTestApi — standalone API module for the In-App Smoke Tests admin tab
 * (services/api.ts is frozen; mirrors the clientHubApi precedent). The types
 * are the machine format from refactor/smoke-test-authoring-standard.md.
 */
import axios from 'axios';

const api = axios.create({ baseURL: '/api', withCredentials: true });

export type ItemStatus = 'pass' | 'fail' | 'skip' | 'blocked';
export type CaptureKind =
  | 'api' | 'console_error' | 'unhandled_error' | 'route_change'
  | 'user_interaction' | 'render' | 'state_snapshot' | 'screenshot';

export interface SmokeItem {
  id: string;
  title: string;
  expected?: string;
  /** Ordered "what to do" — rendered as a numbered list in the run HUD. */
  steps?: string[];
  target_route?: string;
  prereq?: string;
  side_effects?: string[];
  capture_requirements?: string[];
}
export interface SmokeSection { id: string; title: string; items: SmokeItem[]; }
export interface SmokeDefinition {
  id?: number;
  slug: string;
  title: string;
  description?: string;
  scope_tag?: string;
  version?: number;
  sections: SmokeSection[];
  is_archived?: number;
  updated_at?: string;
  last_run?: { run_token: string; status: string; started_at: string; completed_at: string | null } | null;
}
export interface RunDevLog {
  json: any;
  html: string;
  screenshots: Array<{ item_id: string | null; label: string; dataUrl: string }>;
}
export interface SmokeItemResult {
  section_id: string; item_id: string; status: ItemStatus;
  note?: string | null; linked_ticket_id?: number | null; checked_at?: string;
}
export interface SmokeRun {
  run: { run_token: string; definition_id: number; environment: string; status: string; started_at: string; completed_at?: string | null; summary?: { pass: number; fail: number; skip: number; blocked: number } | null; notes?: string | null };
  definition: SmokeDefinition;
  results: SmokeItemResult[];
}
export interface CapabilityGap { section_id: string; item_id: string; missing: string[]; }

export const smokeTestApi = {
  listDefinitions: (includeArchived = false): Promise<SmokeDefinition[]> =>
    api.get('/smoke-tests', { params: { includeArchived } }).then(r => r.data.data),

  getDefinition: (slug: string): Promise<SmokeDefinition> =>
    api.get(`/smoke-tests/${slug}`).then(r => r.data.data),

  importDefinition: (def: SmokeDefinition): Promise<SmokeDefinition> =>
    api.post('/smoke-tests/import', def).then(r => r.data.data),

  archiveDefinition: (id: number): Promise<{ archived: boolean }> =>
    api.post(`/smoke-tests/definitions/${id}/archive`).then(r => r.data.data),

  capabilities: (): Promise<{ capabilities: CaptureKind[]; capture_kinds: CaptureKind[] }> =>
    api.get('/smoke-tests/capabilities').then(r => r.data.data),

  gaps: (slug: string): Promise<{ gaps: CapabilityGap[] }> =>
    api.get(`/smoke-tests/${slug}/gaps`).then(r => r.data.data),

  startRun: (slug: string, environment?: string): Promise<{ run_token: string; definition: SmokeDefinition }> =>
    api.post('/smoke-tests/runs', { slug, environment }).then(r => r.data.data),

  getRun: (token: string): Promise<SmokeRun> =>
    api.get(`/smoke-tests/runs/${token}`).then(r => r.data.data),

  saveItemResult: (token: string, input: { section_id: string; item_id: string; status: ItemStatus; note?: string; linked_ticket_id?: number | null; evidence?: any }): Promise<{ ok: boolean }> =>
    api.post(`/smoke-tests/runs/${token}/items`, input).then(r => r.data.data),

  attachCapture: (token: string, input: { item_id?: string | null; capture_json?: any; capture_html?: string }): Promise<{ ok: boolean }> =>
    api.post(`/smoke-tests/runs/${token}/captures`, input).then(r => r.data.data),

  finalizeRun: (token: string, notes?: string): Promise<SmokeRun> =>
    api.post(`/smoke-tests/runs/${token}/finalize`, { notes }).then(r => r.data.data),

  exportRun: (token: string): Promise<{ json: any; markdown: string }> =>
    api.get(`/smoke-tests/runs/${token}/export`).then(r => r.data.data),

  devLog: (token: string): Promise<RunDevLog> =>
    api.get(`/smoke-tests/runs/${token}/devlog`).then(r => r.data.data),
};

export default smokeTestApi;
